/** API endpoint constants & axios instance. */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ── Dual-auth client glue ────────────────────────────────────────────
// Cookie-based sessions are the primary path (HTTP-only, cleaner CSRF
// posture). Safari with strict tracking-prevention / ITP routinely drops
// the cookie on cross-origin XHRs even when SameSite=Lax allows it, so
// we duplicate the session token into localStorage and resend it as
// `Authorization: Bearer <token>` on every request. The backend's
// `get_current_user` accepts either source — see /app/backend/auth.py.
const TOKEN_KEY = "frisbeex_session_token";
export const setAuthToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* incognito / sandbox / SSR — silently no-op */ }
};
export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || null; }
  catch { return null; }
};

export const api = axios.create({ baseURL: API, withCredentials: true });

// Request interceptor — attach the Bearer fallback for every API call so
// even Safari users keep their session through every navigation.
api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// Response interceptor — when the backend revokes our session (401) we
// drop the stale token from localStorage so the next mount of the auth
// context doesn't replay it forever.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      setAuthToken(null);
    }
    return Promise.reject(err);
  }
);

// Sequences
export const listSequences = () => api.get("/sequences").then((r) => r.data);
export const getStats = () => api.get("/sequences/stats").then((r) => r.data);
export const createSequence = (payload) => api.post("/sequences", payload).then((r) => r.data);
export const fetchCameraProfiles = () => api.get("/camera-profiles").then((r) => r.data);
export const fetchAnchorPoolStats = () => api.get("/training/anchor-pool-stats").then((r) => r.data);
export const fetchHomographySourceStatus = () => api.get("/admin/homography-source").then((r) => r.data);
export const setHomographySourceMode = (mode) =>
  api.post("/admin/homography-source", { mode }).then((r) => r.data);
export const startShadowBackfillAll = () => api.post("/admin/shadow-backfill-all").then((r) => r.data);
export const getSequence = (id) => api.get(`/sequences/${id}`).then((r) => r.data);
export const autoCalibrate = (id, atTimeS = null) =>
  api.post(`/sequences/${id}/auto-calibrate`, atTimeS != null ? { at_time_s: atTimeS } : {}).then((r) => r.data);
export const deleteSequence = (id) => api.delete(`/sequences/${id}`).then((r) => r.data);

// Modal serverless preflight — used by the Pipeline Health Banner's
// recovery CTA. Returns { ok, code, message, ... } never throws — the
// UI inspects `ok` + `code` to drive the toast variant.
export const testModalConnection = () =>
  api.post("/admin/modal/test-connection").then((r) => r.data);

// Phase G · Live-Rollback pipeline flag overrides. The GET returns
// the resolved state of all three flags + their resolution source
// (db_override vs env_default). The POST writes a single override
// — pass `enabled: null` to CLEAR the override (revert to env).
export const getPipelineSettings = () =>
  api.get("/admin/pipeline-settings").then((r) => r.data);
export const updatePipelineSetting = (key, enabled) =>
  api.post("/admin/pipeline-settings", { key, enabled }).then((r) => r.data);

// Analytics (Cockpit) reads — strict no-write surface.
export const getPassingFlowMap = () => api.get("/analytics/passing-flow-map").then((r) => r.data);
export const getPassStats = () => api.get("/analytics/pass-stats").then((r) => r.data);

// 720p Proxy — polled by the Calibration wizard banner so the admin sees
// "encoding 720p · 23s elapsed" → "proxy ready ⚡" without refreshing.
// Per-clip Pass-Vector — drawn in SequenceLab, rendered on the Flow-Map.
export const saveClipPassVector = (clipId, start, end, releaseT = null, imgRef = null) =>
  api.post(`/clips/${clipId}/pass-vector`, {
    start, end,
    ...(releaseT != null ? { release_t: releaseT } : {}),
    // 🆕 Pixelraum-Vertrag: Anzeigeraum der Pins mitschicken, Backend
    // normiert in den kanonischen Sequenz-Raum.
    ...(imgRef?.w > 2 ? { img_ref: imgRef } : {}),
  }).then((r) => r.data);
export const clearClipPassVector = (clipId) =>
  api.delete(`/clips/${clipId}/pass-vector`).then((r) => r.data);

// ── HITL Pass-Vector Triage (Phase A · Schritt 2) ──────────────────
// Wraps the new endpoints introduced for the auto-proposed list. Each
// clip can carry MANY pass_vectors[]; the UI shows their triage badges
// and the admin Accept / Edit / Reject / Delete them. The legacy
// singular `pass_vector` lives alongside (helpers above) for backwards
// compatibility with clips drawn before the heuristic landed.
export const listClipPassVectors = (clipId) =>
  api.get(`/clips/${clipId}/pass-vectors`).then((r) => r.data.pass_vectors || []);
export const verifyClipPassVector = (clipId, vectorId) =>
  api.post(`/clips/${clipId}/pass-vectors/${vectorId}/verify`).then((r) => r.data.pass_vector);
export const rejectClipPassVector = (clipId, vectorId) =>
  api.post(`/clips/${clipId}/pass-vectors/${vectorId}/reject`).then((r) => r.data.pass_vector);
export const correctClipPassVector = (clipId, vectorId, start, end) =>
  api.put(`/clips/${clipId}/pass-vectors/${vectorId}`, { start, end }).then((r) => r.data.pass_vector);
export const deleteClipPassVector = (clipId, vectorId) =>
  api.delete(`/clips/${clipId}/pass-vectors/${vectorId}`).then((r) => r.data);
export const createClipPassVector = (clipId, start, end, releaseT = null, catchT = null) => {
  const payload = { start, end };
  if (releaseT != null) payload.release_t = releaseT;
  if (catchT != null) payload.catch_t = catchT;
  return api.post(`/clips/${clipId}/pass-vectors`, payload).then((r) => r.data.pass_vector);
};
// Player Network Graph (Phase A · Tactical Insight) — verified pass
// hierarchy across all matches (or filtered to one match).
export const fetchPlayerNetwork = (matchId) => {
  const params = matchId ? { match_id: matchId } : {};
  return api.get("/analytics/player-network", { params }).then((r) => r.data);
};

// Phase A · Step 3 · re-run the data harvester for a single verified
// vector. Useful when B2 was offline during the first Accept click.
export const triggerHarvest = (clipId, vectorId) =>
  api.post(`/clips/${clipId}/pass-vectors/${vectorId}/harvest`).then((r) => r.data);

// 🆕 Phase E v8c · Frame-Perfect R/C Override. Update a vector's
// release_t and/or catch_t from the disc-annotation thumbnail-strip
// context menu — backend recomputes H-delta + length_m using the
// same pixel coords, wipes the frame cache when release_t shifts
// more than 0.05 s, and returns the mutated vector + a flag.
export const setPassVectorTimestamps = (clipId, vectorId, { releaseT, catchT } = {}) => {
  const payload = {};
  if (releaseT != null) payload.release_t = releaseT;
  if (catchT != null) payload.catch_t = catchT;
  return api
    .post(`/clips/${clipId}/pass-vectors/${vectorId}/timestamps`, payload)
    .then((r) => r.data);
};

// Bulk-Triage (Phase A · Step 4) · accept every unverified vector with
// `confidence >= minConfidence` in one click. Auto-queues the harvester
// for each newly-verified vector. Returns `{verified_count, verified_ids,
// skipped_count, skipped_low_confidence, harvest_queued}`.
export const bulkVerifyClipPassVectors = (clipId, minConfidence) =>
  api.post(`/clips/${clipId}/pass-vectors/bulk-verify`, {
    min_confidence: minConfidence,
  }).then((r) => r.data);

// Bulk-Reject (Phase A · Step 5) · sweep low-confidence heuristic noise
// off the triage board with one click. Flips every unverified vector with
// `confidence < maxConfidence` to status="rejected" (no harvest queued,
// rejected vectors aren't part of the training corpus). Returns
// `{rejected_count, rejected_ids, skipped_count, skipped_high_confidence}`.
export const bulkRejectClipPassVectors = (clipId, maxConfidence) =>
  api.post(`/clips/${clipId}/pass-vectors/bulk-reject`, {
    max_confidence: maxConfidence,
  }).then((r) => r.data);

// Admin · Dataset-Stats (Phase B prep) · returns the size + health of the
// verified-pass training corpus on disk + B2, plus drift warnings between
// the Mongo manifest counts and the on-disk slice files.
export const getDatasetStats = () =>
  api.get("/admin/dataset-stats").then((r) => r.data);

// Admin · Training-Runs (Phase B · Step 1) · custom YOLOv8 training
// orchestration.  Default `mode="simulated"` runs a deterministic ~30s
// fake fit so the dashboard chart is meaningful even with the current
// 4-sample corpus.  When the corpus crosses 150+ images, switch to
// `mode="real"` to invoke `yolo task=detect mode=train`.
export const listTrainingRuns = (params = {}) =>
  api.get("/admin/training-runs", { params }).then((r) => r.data);
export const getTrainingRun = (id) =>
  api.get(`/admin/training-runs/${id}`).then((r) => r.data);
export const createTrainingRun = (body) =>
  api.post("/admin/training-runs", body).then((r) => r.data);
export const cancelTrainingRun = (id) =>
  api.post(`/admin/training-runs/${id}/cancel`).then((r) => r.data);
export const deleteTrainingRun = (id) =>
  api.delete(`/admin/training-runs/${id}`).then((r) => r.data);

// Phase B · Step 4 — MLOps Hub
//   - promote a completed run as the active inference model
//   - read the currently active model pointer for the dashboard badge
export const promoteTrainingRun = (id) =>
  api.post(`/admin/training-runs/${id}/promote`).then((r) => r.data);
export const getActiveModel = () =>
  api.get("/admin/active-model").then((r) => r.data);

// Phase B · Step 2 · Native Disc Annotation — list extracted frames
// for a harvested pass-vector and the coach's current per-frame bbox
// annotations. Calling this endpoint lazily extracts the 15 JPGs from
// the slice.mp4 cache the first time.
export const listPassVectorFrames = (clipId, vectorId) =>
  api.get(`/clips/${clipId}/pass-vectors/${vectorId}/frames`).then((r) => r.data);

// Absolute URL for a single extracted frame JPG. Used by the canvas
// `<img>` tag — we deliberately bypass axios so the browser caches
// the bytes natively (no JSON overhead per frame nav).
//
// `version` is the cache-busting token stamped onto each frame entry
// by the /frames route (= JPG mtime). Passing it makes the URL
// content-addressable so the browser drops its stale cache the
// instant the harvester writes a fresh JPG. Callers that don't have
// the token yet (e.g. legacy code paths) pass nothing and get the
// un-versioned URL which the backend serves with no-cache headers.
export const passVectorFrameImageUrl = (clipId, vectorId, frameIdx, version) => {
  const base = `${BACKEND_URL}/api/clips/${clipId}/pass-vectors/${vectorId}/frames/${frameIdx}/image`;
  return version != null && version !== "" ? `${base}?v=${version}` : base;
};

// Save (bbox=[x1,y1,x2,y2] in source pixel coords) or CLEAR (bbox=null)
// the disc annotation for one frame. Returns the updated full
// `disc_annotations` list so the UI can refresh overlay state.
export const saveDiscAnnotation = (clipId, vectorId, frameIdx, bboxXyxy) =>
  api.post(
    `/clips/${clipId}/pass-vectors/${vectorId}/frames/${frameIdx}/annotate`,
    { bbox_xyxy: bboxXyxy },
  ).then((r) => r.data);

// Phase B · Step 2.5 — generate `status="suggested"` annotations
// between every pair of confirmed anchors via linear interpolation.
export const interpolateDiscTrajectory = (clipId, vectorId) =>
  api.post(
    `/clips/${clipId}/pass-vectors/${vectorId}/frames/interpolate`,
  ).then((r) => r.data);

// Bulk-flip every suggested → confirmed on this vector.
export const acceptDiscSuggestions = (clipId, vectorId) =>
  api.post(
    `/clips/${clipId}/pass-vectors/${vectorId}/frames/accept-suggestions`,
  ).then((r) => r.data);

// 🐛 HOTFIX 2026-02-24-i · force re-cut of the 15-frame slice from
// the CURRENT vector.timestamp_seconds. Use when the canvas shows
// frames from a stale (pre-release_t-hotfix) slice cache.
export const reHarvestPassVector = (clipId, vectorId) =>
  api.post(
    `/clips/${clipId}/pass-vectors/${vectorId}/re-harvest`,
  ).then((r) => r.data);

// Phase C · PR 3 — kick off the 3D parabolic trajectory fit for one
// fully-annotated pass-vector. Returns the populated `trajectory_fit`
// block which the disc-annotation canvas paints as a green SVG
// parabola overlay. Requires ≥ 4 confirmed disc annotations on the
// vector; the server returns 422 with a hint message otherwise.
export const fitPassVectorTrajectory = (clipId, vectorId) =>
  api.post(
    `/clips/${clipId}/pass-vectors/${vectorId}/fit-trajectory`,
  ).then((r) => r.data);

// Phase D · Step 1 — Burst-Annotate Empty: bulk-mark every frame_idx
// in the array as `status="confirmed_empty"` (no bbox). Coach uses
// this to skip clicking through tail-end / pre-release blank frames.
// Returns the updated full `disc_annotations` list + a summary of how
// many rows were added vs preserved vs replaced.
export const burstConfirmEmpty = (clipId, vectorId, frameIdxs) =>
  api.post(
    `/clips/${clipId}/pass-vectors/${vectorId}/burst-confirm-empty`,
    { frame_idxs: frameIdxs },
  ).then((r) => r.data);

// ── Phase F · Async-Job polling client ─────────────────────────────
// The backend's heavy endpoints (stitch-tracks, rerender-mp4, etc.)
// migrated from synchronous-in-the-request-thread to the generic
// async-job pattern (see `/app/backend/modules/job_manager.py`). The
// POST now returns `{ job_id, kind, status: "pending" }` in ~50 ms;
// the actual work runs in a background asyncio task whose progress
// is persisted on the `jobs` Mongo collection.
//
// `fetchJob` is a one-shot GET; `pollJob` wraps it in a setInterval-
// style loop that resolves on terminal status (`done` / `failed`) or
// rejects on `timeoutMs`. Callers pass `onProgress` to surface
// `progress_pct` + `message` into the UI (spinner, progress bar,
// toast id mutation, …).

// 🆕 2026-08-31 · Fehlertext aus einem beliebigen Fehler ziehen.
//
// Zwei Quellen, die nichts voneinander wissen: ein HTTP-Fehler von axios
// traegt die Backend-Begruendung in `response.data.detail`, eine
// `pollJob`-Ablehnung dagegen in `err.detail` — und dort gibt es kein
// `response`. Alle 82 Fangstellen der App lasen nur die erste Variante.
// Auf jedem Job-Pfad (Shot-Erkennung, Klassifikation, Tracking,
// Auto-Kalibrierung, Schnitt-Suche) landete die eigentliche Begruendung
// deshalb im Nichts und der Nutzer sah „… fehlgeschlagen".
//
// Aufgefallen bei der ehrlichen Proxy-Auskunft (DEVELOPMENT.md Falle 18):
// die neue Meldung nennt Zustand, Ursache und Abhilfe — und waere auf
// genau diesen Pfaden verworfen worden.
export const fehlerText = (e, fallback) =>
  e?.response?.data?.detail
  || e?.detail
  || e?.response?.data?.message
  || (e?.message && e.message !== "Job failed" ? e.message : null)
  || fallback;

// One-shot fetch of a job document. Returns the full job (status,
// progress, message, result, error, …) or rejects on HTTP error.
export const fetchJob = (jobId) =>
  api.get(`/jobs/${jobId}`).then((r) => r.data);

/**
 * Poll a backend job until terminal state.
 *
 * @param {string} jobId
 * @param {object} opts
 * @param {(snapshot: {progress_pct:number, message:string, status:string, elapsed_ms:number}) => void} [opts.onProgress]
 *        Called after every poll (including the first one) with the
 *        latest job snapshot — the UI uses this to drive a progress
 *        bar / live status string. Always called at least once.
 * @param {number} [opts.intervalMs=1500]   Cadence between polls.
 * @param {number} [opts.timeoutMs=600000]  Hard upper bound (10 min).
 * @param {number} [opts.maxConsecErrors=3] How many polling errors
 *        in a row before we give up (the backend can hiccup briefly
 *        without the work having actually failed).
 * @returns {Promise<object>} The terminal job document on `done`.
 * @throws  Rejects with an Error whose `.detail` carries the backend
 *        message on `failed`, or `timeout` on timeout.
 */
export const pollJob = (
  jobId,
  {
    onProgress,
    intervalMs = 1500,
    timeoutMs = 600_000,
    maxConsecErrors = 3,
  } = {},
) => new Promise((resolve, reject) => {
  const start = Date.now();
  let consecErrors = 0;
  let cancelled = false;

  const tick = async () => {
    if (cancelled) return;
    try {
      const job = await fetchJob(jobId);
      consecErrors = 0;
      const elapsed = Date.now() - start;
      onProgress?.({
        progress_pct: job.progress_pct ?? 0,
        message: job.message || job.status,
        status: job.status,
        elapsed_ms: elapsed,
      });
      if (job.status === "done") {
        cancelled = true;
        resolve(job);
        return;
      }
      if (job.status === "failed") {
        cancelled = true;
        const err = new Error(job.error || "Job failed");
        err.detail = job.error || "Job failed";
        err.job = job;
        reject(err);
        return;
      }
      if (elapsed > timeoutMs) {
        cancelled = true;
        const err = new Error("Job timeout");
        err.detail = `Job did not finish within ${Math.round(timeoutMs / 1000)} s`;
        err.timeout = true;
        reject(err);
        return;
      }
      setTimeout(tick, intervalMs);
    } catch (httpErr) {
      consecErrors += 1;
      if (consecErrors >= maxConsecErrors) {
        cancelled = true;
        const err = new Error("Polling failed");
        err.detail = httpErr?.response?.data?.detail || httpErr.message || "Polling failed";
        err.cause = httpErr;
        reject(err);
        return;
      }
      // transient — back off a bit and retry
      setTimeout(tick, intervalMs);
    }
  };
  // Kick off immediately so the UI shows feedback within ~50 ms.
  tick();
});


// Phase C · PR 5 — run the Player Re-ID stitcher on a tracked clip.
// Phase F-migrated to async-job — returns the SAME shape coach UIs
// have always consumed (`{ ok, stitch_summary }`) so callers stay
// 0-diff. Internally: POST returns `{job_id}`, we poll until done,
// then unwrap `job.result.stitch_summary`.
export const stitchClipTracks = async (clipId, { onProgress } = {}) => {
  const { job_id: jobId } = await api
    .post(`/clips/${clipId}/stitch-tracks`)
    .then((r) => r.data);
  const job = await pollJob(jobId, { onProgress });
  const result = job.result || {};
  // Defensive: ancient clips on a stale backend MIGHT still get the
  // legacy synchronous response shape. Honour both.
  if (result.stitch_summary) {
    return { ok: true, stitch_summary: result.stitch_summary };
  }
  // Worst case: empty result → fall back to GET /stitch-summary so
  // the UI still re-paints.
  const summary = await fetchStitchSummary(clipId).catch(() => null);
  return { ok: true, stitch_summary: summary?.stitch_summary || null };
};

// Phase C · PR 5 — cheap GET for the persisted summary, used by the
// clip rows on page-load to render the badges without re-running.
export const fetchStitchSummary = (clipId) =>
  api.get(`/clips/${clipId}/stitch-summary`).then((r) => r.data);

// Force a re-render of the annotated MP4 (server overlays YOLO boxes + IDs).
// Phase F-migrated to async-job — same polling contract as stitch.
// Returns `{ ok, export_mp4_path }`.
export const rerenderClipMp4 = async (clipId, { onProgress } = {}) => {
  const { job_id: jobId } = await api
    .post(`/clips/${clipId}/rerender-mp4`)
    .then((r) => r.data);
  const job = await pollJob(jobId, { onProgress });
  const result = job.result || {};
  return { ok: true, export_mp4_path: result.export_mp4_path || null };
};

export const getProxyStatus = (id) => api.get(`/sequences/${id}/proxy-status`).then((r) => r.data);
export const rebuildProxy = (id) => api.post(`/sequences/${id}/proxy/rebuild`).then((r) => r.data);

export const uploadVideo = (id, file, onProgress, signal) =>
  uploadVideoChunked(id, file, onProgress, signal);

/**
 * Direct-to-B2 Multipart Upload.
 *
 * Flow:
 *   1. POST /uploads/initialize   → server opens a multipart session on B2
 *                                   and hands back presigned PUT URLs for
 *                                   every chunk.
 *   2. For each chunk → PUT directly to B2 (browser ↔ Backblaze, the FastAPI
 *      pod is never in the byte path). ETag is read from the response header
 *      (exposed by the bucket CORS policy).
 *   3. POST /uploads/complete     → server tells B2 to stitch parts, then
 *                                   downloads the assembled file once into
 *                                   the local pod cache to run ffprobe and
 *                                   thumbnail extraction.
 *
 * `onProgress(pct)` is called with an integer 0..100 reflecting total bytes
 * actually transferred (XHR `upload.progress` events summed across chunks).
 * The optional `signal` (AbortSignal) lets callers cancel a running upload —
 * the in-flight chunk XHRs are aborted and `/uploads/abort` is fired so B2
 * releases the staged parts.
 */
const CHUNK_SIZE = 16 * 1024 * 1024;   // 16 MB per part (B2 needs ≥5 MB)
const MAX_CONCURRENCY = 3;             // parallel chunk PUTs

export async function uploadVideoChunked(seqId, file, onProgress, signal) {
  const partCount = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

  // 1) Open multipart session on B2.
  const init = await api
    .post("/uploads/initialize", {
      seq_id: seqId,
      filename: file.name,
      content_type: file.type || "video/mp4",
      size: file.size,
      part_count: partCount,
    })
    .then((r) => r.data);

  const { upload_id, key, parts: presigned } = init;
  const partSize = init.part_size || CHUNK_SIZE;

  // Track per-chunk progress so we can report a smooth total %.
  const loaded = new Array(presigned.length).fill(0);
  const reportProgress = () => {
    if (!onProgress) return;
    const total = loaded.reduce((a, b) => a + b, 0);
    onProgress(Math.min(100, Math.round((total * 100) / file.size)));
  };

  const uploadOnePart = ({ part_number, url }) => new Promise((resolve, reject) => {
    const start = (part_number - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    // No Content-Type header — the signature was generated without one, so
    // adding it here would break the signature on some S3-compatible backends.
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        loaded[part_number - 1] = e.loaded;
        reportProgress();
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag");
        if (!etag) {
          reject(new Error(`Part ${part_number}: missing ETag in B2 response (CORS exposes-headers?)`));
          return;
        }
        loaded[part_number - 1] = blob.size;
        reportProgress();
        resolve({ part_number, etag: etag.replaceAll('"', '') });
      } else {
        reject(new Error(`Part ${part_number} failed: HTTP ${xhr.status} ${xhr.responseText?.slice(0, 200)}`));
      }
    };
    xhr.onerror = () => reject(new Error(`Part ${part_number}: network error`));
    xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));

    if (signal) {
      if (signal.aborted) { xhr.abort(); return; }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(blob);
  });

  // 🆕 04.09. · Wiederholversuch je Teil: Ein einzelner Netzwerk-
  // Wackler bei ~50 Teilen brach vorher den GANZEN Upload ab (Abort +
  // komplett neu hochladen). Die signierten URLs gelten 6 h — ein Teil
  // darf also in Ruhe 2× nachversuchen, bevor wirklich Schluss ist.
  // Nutzer-Abbrüche (AbortError) werden nie wiederholt.
  const uploadOnePartMitRetry = async (p) => {
    for (let versuch = 1; ; versuch += 1) {
      try {
        return await uploadOnePart(p);
      } catch (e) {
        if (e?.name === "AbortError" || versuch >= 3) throw e;
        await new Promise((r) => setTimeout(r, versuch * 2000));
      }
    }
  };

  // 2) Upload chunks with a small concurrency window.
  const queue = [...presigned];
  const results = [];
  try {
    const workers = Array.from({ length: Math.min(MAX_CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const p = queue.shift();
        const done = await uploadOnePartMitRetry(p);
        results.push(done);
      }
    });
    await Promise.all(workers);
  } catch (err) {
    // Best-effort cleanup so B2 doesn't keep paid-for orphan parts around.
    try { await api.post("/uploads/abort", { upload_id, key }); } catch (_) { /* ignore */ }
    throw err;
  }

  // 3) Tell the backend to finalise → triggers ffprobe + thumbnail.
  // 🆕 04.09. · Der Abschluss ist der fragilste Schritt (B2-Finish +
  // ~1-GB-Readback im Backend) und seit heute serverseitig IDEMPOTENT:
  // die Teile liegen fertig in B2, ein erneuter Aufruf ist gefahrlos.
  // Deshalb bis zu 4 Versuche mit wachsender Pause, bevor der Fehler
  // den Nutzer erreicht — vorher führte ein einziger transienter
  // 502 dazu, dass das komplette Video neu hochgeladen wurde.
  const completePayload = {
    seq_id: seqId,
    upload_id,
    key,
    filename: file.name,
    content_type: file.type || "video/mp4",
    parts: results.sort((a, b) => a.part_number - b.part_number),
  };
  let completed;
  for (let versuch = 1; ; versuch += 1) {
    try {
      completed = await api
        .post("/uploads/complete", completePayload)
        .then((r) => r.data);
      break;
    } catch (err) {
      if (versuch >= 4) throw err;
      await new Promise((r) => setTimeout(r, versuch * 3000));
    }
  }

  if (onProgress) onProgress(100);
  return completed;
}

export const abortUpload = (upload_id, key) =>
  api.post("/uploads/abort", { upload_id, key }).then((r) => r.data);

export const frameUrl = (id, t = 0) => `${API}/sequences/${id}/frame?t=${t}`;

// 🆕 2026-08 · Kontroll-Kacheln: Anker-Standbild mit projiziertem
// Feldraster bzw. Pass-Standbild mit eingezeichnetem Vektor-Pfeil.
export const anchorPreviewUrl = (seqId, anchorId) =>
  `${API}/sequences/${seqId}/anchors/${anchorId}/preview`;
export const passVectorPreviewUrl = (clipId, vectorId) =>
  `${API}/clips/${clipId}/pass-vectors/${vectorId}/preview`;

// ── 🆕 v1 · Anker-Sammelmodus (Data-Flywheel) ──────────────────────
export const listAnchors = (seqId) =>
  api.get(`/sequences/${seqId}/anchors`).then((r) => r.data);
export const listLineCombos = () =>
  api.get(`/anchor-line-combos`).then((r) => r.data);
export const getP3PLandmarks = (seqId) =>
  api.get(`/sequences/${seqId}/p3p-landmarks`).then((r) => r.data);
export const updateCameraConfig = (seqId, payload) =>
  api.patch(`/sequences/${seqId}/camera-config`, payload).then((r) => r.data);
export const createAnchor = (seqId, payload) =>
  api.post(`/sequences/${seqId}/anchors`, payload).then((r) => r.data);
// 🆕 2026-08-25 · Geister-Nachfuehrung: gezeichnete Linien von einem Frame
// in einen anderen mitziehen. Reine Rechnung, schreibt nichts.
export const reprojectSegments = (seqId, payload) =>
  api.post(`/sequences/${seqId}/segments/reproject`, payload).then((r) => r.data);
// 🆕 2026-08-25 · Linien-Vorschlag des Modells fuer EINEN Frame. Kommt als
// Geist ins Bild und ist damit passiv, bis der Mensch ihn anfasst — der
// Modus HOMOGRAPHY_SOURCE bleibt unberuehrt. Dauert auf der CPU ~15 s.
export const lineProposal = (seqId, t, w, h) =>
  api.get(`/sequences/${seqId}/line-proposal`, { params: { t, w, h }, timeout: 120000 })
    .then((r) => r.data);
// 🆕 2026-08-31 · Shot abtasten und die Frames nach Güte sortiert liefern.
// Laeuft als Job (ein Frame kostet auf der CPU 5–15 s), Fortschritt ueber
// pollJob. Schreibt nichts.
export const scanShotAnchorCandidates = (seqId, shotId, payload) =>
  api.post(`/sequences/${seqId}/shots/${shotId}/anchor-candidates`, payload)
    .then((r) => r.data);
export const getCameraSegments = (seqId, recompute = false) =>
  api.get(`/sequences/${seqId}/camera-segments`, {
    params: recompute ? { recompute: 1 } : {},
  }).then((r) => r.data);
// 2026-09-01 · Naechster-Schritt-Engine (Prozess-Soll Punkt 2)
// 2026-09-01 · Falle 17 sichtbar: ist der Volume-Pool aktuell?
export const getPseudoExportStatus = (overlay = "anchor") =>
  api.get("/training/pseudo-labels/status", { params: { overlay } })
    .then((r) => r.data);
export const getPipelineStatus = (seqId) =>
  api.get(`/sequences/${seqId}/pipeline-status`).then((r) => r.data);
export const getShots = (seqId) =>
  api.get(`/sequences/${seqId}/shots`).then((r) => r.data);
export const detectShots = (seqId) =>
  api.post(`/sequences/${seqId}/detect-shots`).then((r) => r.data);
// 🆕 2026-08 · Shot-Classifier Prototyp (Wide/Close/Replay/Unsicher)
export const classifyShots = (seqId) =>
  api.post(`/sequences/${seqId}/shot-classify`).then((r) => r.data);
export const setShotClass = (seqId, shotId, label) =>
  api.patch(`/sequences/${seqId}/shots/${shotId}/class`, { label }).then((r) => r.data);
// 🆕 2026-08-23 · Verborgene Schnitte: Shot am Abspielkopf teilen, eine
// manuelle Teilung zurücknehmen, und einen Shot gründlich durchsuchen
// (HistogramDetector 0,05 — läuft als Job, siehe modules/shot_cuts.py).
export const splitShot = (seqId, shotId, atTimeS) =>
  api.post(`/sequences/${seqId}/shots/${shotId}/split`, { at_time_s: atTimeS }).then((r) => r.data);
export const unsplitShot = (seqId, shotId) =>
  api.post(`/sequences/${seqId}/shots/${shotId}/unsplit`).then((r) => r.data);
export const scanCutsInShot = (seqId, shotId) =>
  api.post(`/sequences/${seqId}/shots/${shotId}/scan-cuts`).then((r) => r.data);
// 🆕 2026-08-31 · Der Detektor erfindet auch Schnitte: Gegenstück zum
// Teilen. `mergeShotWithPrev` streicht die Detektorgrenze am Anfang
// dieses Shots, `unmergeShot` holt sie zurück. Beides wird als Zeitpunkt
// gespeichert und übersteht jeden neuen Shot-Scan (modules/shot_split.py).
export const mergeShotWithPrev = (seqId, shotId) =>
  api.post(`/sequences/${seqId}/shots/${shotId}/merge-prev`).then((r) => r.data);
export const unmergeShot = (seqId, shotId) =>
  api.post(`/sequences/${seqId}/shots/${shotId}/unmerge`).then((r) => r.data);
// 🆕 2026-08 · „✓ geprüft“: Replay-Link manuell bestätigen (Gold-Label)
export const confirmReplayLink = (seqId, shotId, confirmed = true) =>
  api.patch(`/sequences/${seqId}/shots/${shotId}/replay-confirm`, { confirmed }).then((r) => r.data);
// 🆕 2026-08 · Visueller Frame-Tracker (Optical Flow, Phase 5)
export const trackCamera = (seqId) =>
  api.post(`/sequences/${seqId}/track-camera`).then((r) => r.data);
// 🆕 2026-08 · Ein-Klick-Auto-Kalibrierung (DL-Anker pro Wide-Shot + Tracking)
export const autoCalibrateAll = (seqId) =>
  api.post(`/sequences/${seqId}/auto-calibrate-all`).then((r) => r.data);
// 🆕 Shot-Selbstkalibrierung (Bündelausgleich, VISION.md Prinzip 6)
export const runShotSelfcal = (seqId, payload = {}) =>
  api.post(`/sequences/${seqId}/shot-selfcal`, payload).then((r) => r.data);
// 2026-09-01 · Konsens-Review (Prozess 4a): einmal entscheiden statt ewig warnen
export const konsensOkSetzen = (seqId, shotId) =>
  api.post(`/sequences/${seqId}/shots/${shotId}/konsens-ok`).then((r) => r.data);
export const konsensOkEntfernen = (seqId, shotId) =>
  api.delete(`/sequences/${seqId}/shots/${shotId}/konsens-ok`).then((r) => r.data);
export const selfcalPoseVerwerfen = (seqId, shotId) =>
  api.delete(`/sequences/${seqId}/shot-selfcal/${shotId}`).then((r) => r.data);
export const getShotSelfcal = (seqId) =>
  api.get(`/sequences/${seqId}/shot-selfcal`).then((r) => r.data);
// 🆕 2026-08 · Anker-Feld-Abdeckung (5×3-Raster + projizierte Klicks)
export const getAnchorCoverage = (seqId) =>
  api.get(`/sequences/${seqId}/anchor-coverage`).then((r) => r.data);

export const fitFieldDims = (seqId, payload) =>
  api.post(`/sequences/${seqId}/field-fit`, payload).then((r) => r.data);
// 🆕 2026-08 · Adaptive-Grid-Search-Job (Coarse-to-Fine über W/L_mid);
// liefert `{ok, job_id}` — Frontend pollt via `pollJob` und rendert das
// Job-Result (consensus, fov_hint, boundary_hint, …).
// 2026-09-01 · Feld-Fit-Vorschlag (Prozess 3): lesen + ablehnen
export const getFieldFitVorschlag = (seqId) =>
  api.get(`/sequences/${seqId}/field-fit-vorschlag`).then((r) => r.data);
export const fieldFitVorschlagAblehnen = (seqId) =>
  api.post(`/sequences/${seqId}/field-fit-vorschlag/ablehnen`).then((r) => r.data);
// 🆕 2026-09-02 · Netz-Selfcal Stufe 4 (Vorschlag → Übernahme → Provider)
export const getNetzSelfcalVorschlag = (seqId) =>
  api.get(`/sequences/${seqId}/netz-selfcal-vorschlag`).then((r) => r.data);
export const netzSelfcalVorschlagStarten = (seqId) =>
  api.post(`/sequences/${seqId}/netz-selfcal/vorschlag`).then((r) => r.data);
// 🆕 2026-10-09 · Auftrag 40, R5 (S2-R10): „Posen ohne Klick rechnen" (Schalter S14_ERNTE_PRODUKTWEG)
export const posenOhneKlickStarten = (seqId) =>
  api.post(`/sequences/${seqId}/posen-ohne-klick`).then((r) => r.data);
export const netzSelfcalVorschlagUebernehmen = (seqId, shotIds) =>
  api.post(`/sequences/${seqId}/netz-selfcal-vorschlag/uebernehmen`,
    shotIds ? { shot_ids: shotIds } : {}).then((r) => r.data);
export const netzSelfcalVorschlagAblehnen = (seqId) =>
  api.post(`/sequences/${seqId}/netz-selfcal-vorschlag/ablehnen`).then((r) => r.data);
export const deleteNetzSelfcal = (seqId, shotId) =>
  api.delete(`/sequences/${seqId}/netz-selfcal/${shotId}`).then((r) => r.data);
export const adaptFieldDims = (seqId, payload) =>
  api.post(`/sequences/${seqId}/adapt-field-dims`, payload || {}).then((r) => r.data);
export const deleteAnchor = (seqId, anchorId) =>
  api.delete(`/sequences/${seqId}/anchors/${anchorId}`).then((r) => r.data);
// 🆕 Auftrag 29, R2/R4 · Klickliste im Anker-Sammelmodus
export const getKlickliste = (name) =>
  api.get(`/klicklisten/${encodeURIComponent(name)}`).then((r) => r.data);
export const setzeUnklickbar = (name, nr, notiz) =>
  api.post(`/klicklisten/${encodeURIComponent(name)}/${nr}/unklickbar`, { notiz })
    .then((r) => r.data);
export const nimmUnklickbarZurueck = (name, nr) =>
  api.delete(`/klicklisten/${encodeURIComponent(name)}/${nr}/unklickbar`)
    .then((r) => r.data);
// 🆕 Auftrag LP1, R2–R4 · Linienprüfung im Anker-Sammelmodus
export const getLinienpruefung = (liste, seqId) =>
  api.get(`/linienpruefung/${encodeURIComponent(liste)}/${encodeURIComponent(seqId)}`)
    .then((r) => r.data);
export const speichereLinienUrteil = (liste, seqId, nr, body) =>
  api.put(`/linienpruefung/${encodeURIComponent(liste)}/${encodeURIComponent(seqId)}/${nr}/urteil`, body)
    .then((r) => r.data);
export const entferneLinienUrteil = (liste, seqId, nr, kennung) =>
  api.delete(`/linienpruefung/${encodeURIComponent(liste)}/${encodeURIComponent(seqId)}/${nr}/urteil/`
    + `${encodeURIComponent(kennung)}`).then((r) => r.data);
export const exportLinienpruefung = (liste, seqId) =>
  api.get(`/linienpruefung/${encodeURIComponent(liste)}/${encodeURIComponent(seqId)}/export`)
    .then((r) => r.data);
// 🆕 Auftrag 57 · Klickstrecke Bodenklicks an Ankerbildern (nur hinter REACT_APP_BODENKLICK_STRECKE=1)
export const getBodenklickStrecke = (seqId) =>
  api.get(`/bodenklicks/${encodeURIComponent(seqId)}/strecke`).then((r) => r.data);
export const speichereBodenklicks = (seqId, ankerId, body) =>
  api.put(`/bodenklicks/${encodeURIComponent(seqId)}/${encodeURIComponent(ankerId)}`, body).then((r) => r.data);
export const loescheBodenklicks = (seqId, ankerId) =>
  api.delete(`/bodenklicks/${encodeURIComponent(seqId)}/${encodeURIComponent(ankerId)}`).then((r) => r.data);
export const setzeBodenklickHalbzeit = (seqId, grenzeS) =>
  api.put(`/bodenklicks/${encodeURIComponent(seqId)}/halbzeit`, { grenze_s: grenzeS }).then((r) => r.data);
// 🆕 Auftrag 58, R4 · Anker-Prüfliste (gleicher Schalter)
export const getAnkerPruefliste = (seqId) =>
  api.get(`/bodenklicks/${encodeURIComponent(seqId)}/pruefliste`).then((r) => r.data);
// 🆕 Auftrag 60, R4 · Namensregel Spiel/Halbzeit (Hinweis beim Import, gleicher Schalter)
export const pruefeVideoName = (name) =>
  api.get("/bodenklicks/namensregel", { params: { name } }).then((r) => r.data);
export const exportBodenklicks = (seqId) =>
  api.get(`/bodenklicks/${encodeURIComponent(seqId)}/export`).then((r) => r.data);
export const resolveSegOnlyAnchors = (seqId) =>
  api.post(`/sequences/${seqId}/resolve-seg-only`).then((r) => r.data);
// 🆕 2026-08-31 · `overlay` waehlt die Quelle der Homographie-Overlays.
// "anchor" = bisheriger Pool, "selfcal" = zweiter Pool aus dem
// Buendelausgleich (DEVELOPMENT.md Falle 23). Die Pools liegen getrennt,
// damit man sie gegeneinander trainieren kann.
export const exportPseudoLabels = (overlay = "anchor") =>
  api.post(`/training/pseudo-labels/export`, null, { params: { overlay } })
    .then((r) => r.data);
export const thumbnailUrl = (id, t = 2) => `${API}/sequences/${id}/thumbnail?t=${t}`;
/** Pre-warm a codec-safe thumbnail (used after upload completes). */
export const fetchThumbnail = (id, t = 2) =>
  api.post(`/sequences/${id}/thumbnail`, null, { params: { t }, responseType: "blob" }).then((r) => r.data);
export const videoUrl = (id) => `${API}/sequences/${id}/video`;

export const calibrate = (id, payload) => api.post(`/sequences/${id}/calibrate`, payload).then((r) => r.data);

// Clips (= "Sequences" in UI)
export const listClips = (sequenceId) => api.get(`/sequences/${sequenceId}/clips`).then((r) => r.data);
export const createClip = (sequenceId, payload) => api.post(`/sequences/${sequenceId}/clips`, payload).then((r) => r.data);
export const getClip = (clipId) => api.get(`/clips/${clipId}`).then((r) => r.data);
export const updateClip = (clipId, payload) => api.put(`/clips/${clipId}`, payload).then((r) => r.data);
export const deleteClip = (clipId) => api.delete(`/clips/${clipId}`).then((r) => r.data);
export const startClipTracking = (clipId) => api.post(`/clips/${clipId}/track`).then((r) => r.data);
export const getClipJob = (clipId) => api.get(`/clips/${clipId}/job`).then((r) => r.data);
export const getClipTracking = (clipId) => api.get(`/clips/${clipId}/tracking`).then((r) => r.data);
export const setClipLabels = (clipId, payload) => api.put(`/clips/${clipId}/labels`, payload).then((r) => r.data);
export const clipExportUrl = (clipId, kind) => `${API}/clips/${clipId}/export/${kind}`;
/** Inline-streaming URL for the annotated clip MP4 — used by hover-preview
 *  `<video>` tags in the Library / Sequences list / Dashboard heatmap.
 *  The endpoint 302-redirects to a B2 presigned URL with inline content
 *  disposition so the browser streams it into a `<video>` instead of
 *  triggering a download dialog. First hit per clip can take 5-10 s
 *  (lazy render with bounding-box overlays); subsequent hits are <200 ms. */
export const clipPreviewUrl = (clipId) => `${API}/clips/${clipId}/preview.mp4`;

/** Programmatic file download.
 *  The backend now returns a 302 redirect to a presigned Backblaze URL that
 *  already carries `Content-Disposition: attachment; filename="..."`, so the
 *  browser saves the file directly. We just need to navigate the browser to
 *  the API URL via a hidden anchor click — fetch+blob would lose the
 *  Content-Disposition header on cross-origin redirects.
 */
export async function downloadFile(url, _filename) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { document.body.removeChild(a); } catch (_) { /* already removed */ }
  }, 1_000);
}

// Legacy (kept for back-compat in older components)
export const startTracking = (id, payload) => api.post(`/sequences/${id}/track`, payload).then((r) => r.data);
export const getJob = (id) => api.get(`/sequences/${id}/job`).then((r) => r.data);
export const getTracking = (id) => api.get(`/sequences/${id}/tracking`).then((r) => r.data);
export const setLabels = (id, payload) => api.put(`/sequences/${id}/labels`, payload).then((r) => r.data);
export const exportUrl = (id, kind) => `${API}/sequences/${id}/export/${kind}`;


// ---------- YouTube Tactic Cutter ----------
export const listTactics = (params = {}) =>
  api.get("/tactics", { params }).then((r) => r.data);
export const listTacticTags = () =>
  api.get("/tactics/tags").then((r) => r.data);
export const createTactic = (payload) =>
  api.post("/tactics", payload).then((r) => r.data);
export const updateTactic = (id, payload) =>
  api.patch(`/tactics/${id}`, payload).then((r) => r.data);
export const deleteTactic = (id) =>
  api.delete(`/tactics/${id}`).then((r) => r.data);
// Demo-login bypass for preview/local testing — see /api/auth/demo-login.
export const demoLogin = () =>
  api.post("/auth/demo-login").then((r) => r.data);
export const tacticsExportUrl = () => `${API}/tactics/export.json`;

// 🆕 05.09. · Ebene 2: Maus-Verfolgungs-Spuren (Gold-Referenz fürs
// Tracking) — siehe components/wizard/SpielerVerfolger.jsx.
export const saveMausSpuren = (sequenceId, payload) =>
  api.post(`/sequences/${sequenceId}/maus-spuren`, payload).then((r) => r.data);
